import { useEffect, useState } from "react";

export const PAGINATION_THRESHOLD = 4;
const PAGE_SIZES = [5, 10, 25];

export type PaginationState<T> = {
  pageItems: T[];
  page: number;
  pageSize: number;
  total: number;
  pageCount: number;
  setPage: (n: number) => void;
  setPageSize: (n: number) => void;
  /** I controlli compaiono solo oltre 4 elementi. */
  show: boolean;
};

export function usePagination<T>(items: T[], initialPageSize = 5): PaginationState<T> {
  const [page, setPageRaw] = useState(1);
  const [pageSize, setPageSizeRaw] = useState(initialPageSize);
  const total = items.length;
  const show = total > PAGINATION_THRESHOLD;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  // Se la lista si accorcia, la pagina corrente non deve restare fuori intervallo.
  useEffect(() => {
    if (page > pageCount) setPageRaw(pageCount);
  }, [page, pageCount]);

  const current = Math.min(page, pageCount);
  const pageItems = show ? items.slice((current - 1) * pageSize, current * pageSize) : items;

  return {
    pageItems,
    page: current,
    pageSize,
    total,
    pageCount,
    setPage: (n) => setPageRaw(Math.min(Math.max(1, n), pageCount)),
    setPageSize: (n) => {
      setPageSizeRaw(n);
      setPageRaw(1);
    },
    show,
  };
}

/** Elenco compatto di numeri di pagina: prima, ultima, la corrente e le vicine, con i puntini. */
function pageNumbers(page: number, pageCount: number): Array<number | "…"> {
  const wanted = new Set([1, pageCount, page - 1, page, page + 1]);
  const nums = [...wanted].filter((n) => n >= 1 && n <= pageCount).sort((a, b) => a - b);
  const out: Array<number | "…"> = [];
  nums.forEach((n, i) => {
    if (i > 0 && n - nums[i - 1] > 1) out.push("…");
    out.push(n);
  });
  return out;
}

export default function Paginator<T>({ state }: { state: PaginationState<T> }) {
  if (!state.show) return null;
  const { page, pageSize, total, pageCount } = state;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const btn = "min-w-8 rounded border border-neutral-300 bg-white px-2 py-1 text-sm hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-40";

  return (
    <nav className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm" aria-label="Paginazione">
      <label className="flex items-center gap-2">
        Righe per pagina
        <select className="rounded border border-neutral-300 px-2 py-1" value={pageSize} onChange={(e) => state.setPageSize(Number(e.target.value))}>
          {PAGE_SIZES.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>
      <span className="text-gray-600">
        {from}–{to} di {total}
      </span>
      <div className="flex items-center gap-1">
        <button type="button" className={btn} aria-label="Prima pagina" disabled={page === 1} onClick={() => state.setPage(1)}>
          «
        </button>
        <button type="button" className={btn} aria-label="Pagina precedente" disabled={page === 1} onClick={() => state.setPage(page - 1)}>
          ‹
        </button>
        {pageNumbers(page, pageCount).map((n, i) =>
          n === "…" ? (
            <span key={`gap-${i}`} className="px-1 text-gray-500">
              …
            </span>
          ) : (
            <button
              key={n}
              type="button"
              className={btn + (n === page ? " border-blue-600 bg-blue-600 text-white hover:bg-blue-600" : "")}
              aria-label={`Pagina ${n}`}
              aria-current={n === page ? "page" : undefined}
              onClick={() => state.setPage(n)}
            >
              {n}
            </button>
          ),
        )}
        <button type="button" className={btn} aria-label="Pagina successiva" disabled={page === pageCount} onClick={() => state.setPage(page + 1)}>
          ›
        </button>
        <button type="button" className={btn} aria-label="Ultima pagina" disabled={page === pageCount} onClick={() => state.setPage(pageCount)}>
          »
        </button>
      </div>
    </nav>
  );
}
